import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { StatService } from '@cargos/sprintpay-services';
import { BehaviorSubject, Observable, catchError, finalize, of, take } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class SiteSpeakAiService {
    private static readonly SCRIPT_ID: string = 'site-speak-ai-script';
    private static readonly STAT_TIMEOUT_MS: number = 2000;

    private readonly document: Document = inject(DOCUMENT);
    private readonly statService: StatService = inject(StatService);
    private readonly isLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private isLoaded: boolean = false;
    private isEvaluatingGate: boolean = false;

    public readonly isLoaded$: Observable<boolean> = this.isLoadedSubject.asObservable();

    public isEnvironmentEnabled(): boolean {
        return environment.siteSpeakAi.enabled;
    }

    public initialize(): void {
        if (this.isLoaded || this.isEvaluatingGate) {
            return;
        }

        if (this.isScriptAlreadyInjected()) {
            this.setLoaded(true);
            return;
        }

        if (!environment.siteSpeakAi.enabled) {
            return;
        }

        const gateName: string = environment.siteSpeakAi.statsigGateName;
        if (!gateName) {
            this.injectScript();
            return;
        }

        this.isEvaluatingGate = true;

        this.statService
            .isFeatureEnabledWhenReady(gateName, environment.siteSpeakAi.enabled, SiteSpeakAiService.STAT_TIMEOUT_MS)
            .pipe(
                take(1),
                catchError(() => of(environment.siteSpeakAi.enabled)),
                finalize(() => {
                    this.isEvaluatingGate = false;
                })
            )
            .subscribe((isEnabled: boolean) => {
                if (!isEnabled || this.isLoaded) {
                    return;
                }

                this.injectScript();
            });
    }

    private injectScript(): void {
        if (this.isScriptAlreadyInjected()) {
            this.setLoaded(true);
            return;
        }

        const script: HTMLScriptElement = this.document.createElement('script');
        script.id = SiteSpeakAiService.SCRIPT_ID;
        script.type = 'text/javascript';
        script.src = environment.siteSpeakAi.scriptUrl;
        script.async = true;

        this.document.head?.appendChild(script);
        this.setLoaded(true);
    }

    private setLoaded(isLoaded: boolean): void {
        this.isLoaded = isLoaded;
        this.isLoadedSubject.next(isLoaded);
    }

    private isScriptAlreadyInjected(): boolean {
        const scriptById: HTMLScriptElement | null = this.document.querySelector(`#${SiteSpeakAiService.SCRIPT_ID}`);

        if (scriptById) {
            return true;
        }

        const scriptBySource: HTMLScriptElement | null = this.document.querySelector(
            `script[src="${environment.siteSpeakAi.scriptUrl}"]`
        );

        return Boolean(scriptBySource);
    }
}
